import { useState } from "react";

import {
  DebitNote,
  DEBIT_NOTE_STATUS_APPROVED_BY_SET,
  DEBIT_NOTE_STATUS_CANCELLATION_FAILED,
  DEBIT_NOTE_STATUS_CANCELLATION_REQUESTED,
  DEBIT_NOTE_STATUS_CANCELLED,
  DEBIT_NOTE_STATUS_ERROR,
  DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  DEBIT_NOTE_STATUS_UPLOADED_TO_SET,
  formatDebitNoteNumber,
} from "../../models/debit-note";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import DebitNoteCrudStatus from "./DebitNoteCrudStatus";
import DebitNoteCrudActions from "./DebitNoteCrudActions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import { DEBIT_NOTES_RETRIEVE } from "../../auth/permissions";

interface DebitNoteCardProps {
  debitNote: DebitNote;
  onXMLClick: (debitNote: DebitNote) => void;
  onPDFClick: (debitNote: DebitNote) => void;
  onCancellationClick: (debitNote: DebitNote) => void;
  onCancellationRetryClick: (_: DebitNote) => void;
  onCancellationRevertClick: (_: DebitNote) => void;
  onUpdateClick: (debitNote: DebitNote) => void;
  onDeleteClick: (debitNote: DebitNote) => void;
  onConsultByCdcClick: (_: DebitNote) => void;
  onResendClick: (_: DebitNote) => void;
  onSendEmailClick: (_: DebitNote) => void;
  onHistoryClick: (_: DebitNote) => void;
}

export function DebitNoteCard({
  debitNote,
  onXMLClick,
  onPDFClick,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}: DebitNoteCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case DEBIT_NOTE_STATUS_UPLOADED_TO_SET:
        return "warning";
      case DEBIT_NOTE_STATUS_ERROR:
        return "danger";
      case DEBIT_NOTE_STATUS_SET_UNKNOWN_ERROR:
        return "danger";
      case DEBIT_NOTE_STATUS_CANCELLATION_FAILED:
        return "danger";
      case DEBIT_NOTE_STATUS_APPROVED_BY_SET:
        return "success";
      case DEBIT_NOTE_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case DEBIT_NOTE_STATUS_CANCELLED:
        return "info";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (debitNote.xmlPath !== undefined) {
    xmlButton = (
      <PermissionRequiredComponent permissionName={DEBIT_NOTES_RETRIEVE}>
        <CTooltip content="XML">
          <CButton
            className="text-white"
            color="info"
            onClick={() => {
              onXMLClick(debitNote);
            }}
          >
            <i className="fa fa-code"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  if (debitNote.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={DEBIT_NOTES_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(debitNote);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(debitNote.status)}
      >
        <span>
          <strong>{formatDebitNoteNumber(debitNote)}</strong>
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>
            {formatUTCDateTime(new Date(debitNote.debitNoteDate!))}
          </strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {debitNote.clientSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {debitNote.clientRuc}
          </p>
          <p>
            <strong>Monto:</strong>{" "}
            {formatToCurrency(debitNote.amount!, debitNote.currency!)}
          </p>
          <p>
            <strong>Moneda:</strong> {CURRENCY_CHOICES.get(debitNote.currency!)}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {debitNote.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong>{" "}
            <DebitNoteCrudStatus debitNote={debitNote} />
          </p>
          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {debitNote.pdfPath && debitNote.id !== undefined && <DocumentPrintButton kind="debit-note" documentId={debitNote.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <DebitNoteCrudActions
              debitNote={debitNote}
              onCancellationClick={onCancellationClick}
              onCancellationRetryClick={onCancellationRetryClick}
              onCancellationRevertClick={onCancellationRevertClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onConsultByCdcClick={onConsultByCdcClick}
              onResendClick={onResendClick}
              onSendEmailClick={onSendEmailClick}
              onHistoryClick={onHistoryClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
